import type { PlanFinding, PlanVersionSummary } from '@/lib/apiTypes'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { Tooltip } from '@/shared/components/Tooltip'
import { ActionChips } from '@/features/plans/components/ActionChips'
import { AttestationStatus } from '@/features/plans/components/AttestationStatus'
import { StalenessChip } from '@/features/plans/components/StalenessChip'
import { affectedCount } from '@/features/plans/lib/counts'
import { driftFindings, redactedCell, replacementCauseMap, statefulCount } from '@/features/plans/lib/findings'
import { STALE_STYLE, staleness } from '@/features/plans/lib/staleness'

export function PlanSummaryTab({ orgId, workspaceName, version, findings, currentSerial, loading }: { orgId: string; workspaceName: string; version: PlanVersionSummary; findings: PlanFinding[]; currentSerial: number | null | undefined; loading: boolean }) {
  const drift = driftFindings(findings)
  const stateful = statefulCount(findings)
  const replaceCauses = replacementCauseMap(findings)
  const stale = staleness(version.prior_serial, currentSerial)

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* Action counts + blast radius */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <ActionChips v={version} size="0.85rem" />
        <span className="badge badge-neutral" style={{ fontSize: '0.62rem' }} title="Blast radius">
          {affectedCount(version)} resources · {stateful} stateful · {version.destroy_count} destroys
        </span>
        {version.replace_count > 0 && <span className="badge badge-amber" style={{ fontSize: '0.62rem' }}>{version.replace_count} replaced</span>}
      </div>

      {/* Verdict chips */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <StalenessChip priorSerial={version.prior_serial} currentSerial={currentSerial} />
        <span style={{ fontSize: '0.7rem', color: STALE_STYLE[stale.tone].color }}>{stale.label}</span>
        <span style={{ flex: 1 }} />
        <AttestationStatus orgId={orgId} workspaceName={workspaceName} version={version} />
        {version.terraform_version && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)' }}>tf {version.terraform_version}</span>
        )}
      </div>

      {/* Resource drift */}
      <div>
        <div className="eyebrow">
          Resource drift{version.drift_count > 0 ? ` · ${version.drift_count}` : ''}
        </div>
        {loading ? (
          <SkeletonTable headers={['Address', 'Provider', 'Before', 'After']} rows={3} firstColTwoLines={false} />
        ) : drift.length === 0 ? (
          <EmptyState icon={version.drift_count > 0 ? '◈' : '✓'} title={version.drift_count > 0 ? 'Drift detected, awaiting scan detail.' : 'No drift detected.'} subtitle={version.drift_count > 0 ? 'The plan scanner (M3) populates per-resource drift here.' : undefined} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th><Tooltip text="Resource address that drifted, with a badge when the change forces a replace." placement="top"><span tabIndex={0} className="cursor-help">Address</span></Tooltip></th><th><Tooltip text="Provider that owns the drifted resource." placement="top"><span tabIndex={0} className="cursor-help">Provider</span></Tooltip></th><th><Tooltip text="Recorded value in state before the plan." placement="top"><span tabIndex={0} className="cursor-help">Before</span></Tooltip></th><th><Tooltip text="Value the plan found or proposes." placement="top"><span tabIndex={0} className="cursor-help">After</span></Tooltip></th></tr></thead>
              <tbody>
                {drift.map((f) => (
                  <tr key={f.id}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text2)' }}>
                      {f.resource ?? '-'}
                      {replaceCauses[f.resource ?? ''] && (
                        <span className="badge badge-amber" style={{ marginLeft: 6, fontSize: '0.52rem' }} title={replaceCauses[f.resource ?? '']}>⟳ replace</span>
                      )}
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--text3)' }}>{typeof f.meta?.provider === 'string' ? f.meta.provider : '-'}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--text3)' }}>{redactedCell(f.meta?.before)}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', color: 'var(--text)' }}>{redactedCell(f.meta?.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', marginTop: 8 }}>
          // before/after are redacted server-side — only change shape is shown, never a concrete value.
        </div>
      </div>
    </div>
  )
}
